import type {UseQueryResult} from '@tanstack/react-query'
import {useCallback, useMemo} from 'react'

import type {Row} from '../../hooks/tanstack-table/helpers/types'
import type {MemexItemModel} from '../../models/memex-item-model'
import {getPageTypeFromQueryKey, isPageTypeForSubIssues} from '../../state-providers/memex-items/queries/query-keys'
import type {MemexSubIssuesQueryData} from '../../state-providers/memex-items/queries/types'
import {usePaginatedMemexItemsQuery} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query'

export const useSubIssuesLoading = () => {
  const {queriesForSubIssues, queryKeysForSubIssues} = usePaginatedMemexItemsQuery()

  // Map of parent issue ID to the corresponding active sub-issues query
  const subIssueQueriesByParentId = useMemo(() => {
    const queriesByParentId: Record<string, UseQueryResult<MemexSubIssuesQueryData>> = {}

    for (const [index, queryKey] of queryKeysForSubIssues.entries()) {
      const pageType = getPageTypeFromQueryKey(queryKey)
      if (isPageTypeForSubIssues(pageType)) {
        const parentId = pageType.parentIssueId

        const query = queriesForSubIssues[index]
        if (query) {
          queriesByParentId[parentId] = query
        }
      }
    }

    return queriesByParentId
  }, [queryKeysForSubIssues, queriesForSubIssues])

  /**
   * Checks if the sub-issues for a given parent issue row are currently loading.
   * @param row The parent issue row to check.
   */
  const isLoadingSubIssues = useCallback(
    (row: Row<MemexItemModel>): boolean => {
      const parentId = row.original.content.id.toString()
      return !!subIssueQueriesByParentId[parentId]?.isLoading
    },
    [subIssueQueriesByParentId],
  )

  /**
   * Returns the count of sub-issues queries that are currently loading.
   * Used to get a count of how many SubIssuesPlaceholderRows are rendered in the table (1 per parent).
   */
  const getCountLoadingSubIssues = useCallback((): number => {
    const loadingQueries = queriesForSubIssues.filter(query => query.isLoading)
    return loadingQueries.length
  }, [queriesForSubIssues])

  /**
   * Returns the count of rows that are currently missing sub-issues.
   * Sub-issues may be missing if they are not in the project, or if the user does not have read permission for them.
   * Used to get a count of how many SubIssuesMissingRows are rendered in the table (1 per parent).
   * @param rows All rows in the table.
   */
  const getCountMissingSubIssues = useCallback(
    (rows: Array<Row<MemexItemModel>>): number => {
      const rowsWithMissingSubIssues = rows.filter(
        row => row.getIsExpanded() && !isLoadingSubIssues(row) && (row.subIssueData?.missingSubIssues ?? 0) > 0,
      )
      return rowsWithMissingSubIssues.length
    },
    [isLoadingSubIssues],
  )

  return {
    isLoadingSubIssues,
    getCountLoadingSubIssues,
    getCountMissingSubIssues,
  }
}
